import React, { useEffect, useState, useRef } from "react"
import { useDispatch } from "react-redux"
import { Modal, makeStyles, TextField } from "@material-ui/core"
import { CircularProgress } from "@material-ui/core"
import { green } from "@material-ui/core/colors"
import { Button } from "bossfotos-design-system"
import RevolutCheckout from "@revolut/checkout"

import { amountInCents } from "../v2_actions/math_utils"
import { revolutTrolley } from "../v2_actions/revolut_trolley"
import { trolleyProcessPayment } from "../v2_actions/trolley_process_payment"

import { sentryApi } from "./o11y/sentryApi"


export const RevolutPayment = ({
  onClose,
  totalPriceOwingByCustomer,
  galleryCurrencyCode,
  trolleyId,
  lastName,
  email,
  setOpenSnackBar,
  onMessage,
  setDisableCheckoutButton,
  onPaymentSuccessfulAndConfirmed,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [processingPayment, setProcessingPayment] = useState(false)
  const [walletAvailable, setWalletAvailable] = useState(false)
  const [cardholderName, setCardholderName] = useState("")
  const publicId = useRef(null)
  const cardFieldRef = useRef(null)
  const paymentAmount = amountInCents(totalPriceOwingByCustomer)
  const currencyCode = galleryCurrencyCode.toUpperCase()
  const revolutMode = window.LOCAL_SETTINGS.REVOLUT_MODE

  useEffect(() => {
    const publicToken = window.LOCAL_SETTINGS.REVOLUT_PUBLIC_KEY

    RevolutCheckout.payments({
      locale: "en",
      publicToken: publicToken,
      mode: revolutMode,
    }).then((paymentInstance) => {
      const walletTarget = document.getElementById("revolut-wallet-buttons")
      const paymentRequestInstance = paymentInstance.paymentRequest(walletTarget, {
        currency: currencyCode,
        amount: paymentAmount,
        createOrder: () => {
          return dispatch(revolutTrolley.prepareOrder({
            trolley_id: trolleyId,
            total_price: paymentAmount,
          })).then((order) => {
            publicId.current = order.public_id
            return { publicId: order.public_id }
          })
        },
        onSuccess: () => onPaymentSuccess(),
        onError: (message) => onPaymentError(message),
      })

      paymentRequestInstance.canMakePayment().then((method) => {
        if (method) {
          paymentRequestInstance.render()
          setWalletAvailable(true)
        } else {
          paymentRequestInstance.destroy()
        }
      })
    }).catch((error) => {
      onGeneralError(error)
    })

    dispatch(revolutTrolley.prepareOrder({
      trolley_id: trolleyId,
      total_price: paymentAmount,
    })).then((order) => {
      publicId.current = order.public_id

      RevolutCheckout(order.public_id, revolutMode).then((checkoutInstance) => {
        const cardTarget = document.getElementById("revolut-card-field")
        const cardField = checkoutInstance.createCardField({
          target: cardTarget,
          hidePostcodeField: true,
          onSuccess: () => onPaymentSuccess(),
          onError: (message) => onPaymentError(message),
          onCancel: () => onPaymentCancel(),
          onValidation: () => {},
        })
        cardFieldRef.current = cardField
        setLoading(false)
      })
    }).catch((error) => {
      onGeneralError(error)
    })

    return () => {
      if (cardFieldRef.current) {
        cardFieldRef.current.destroy()
      }
    }
  }, [])

  const handlePayWithCard = () => {
    if (!cardFieldRef.current) return
    setSubmitting(true)
    cardFieldRef.current.submit({
      name: cardholderName,
      email: email,
    })
  }

  const onPaymentSuccess = () => {
    setProcessingPayment(true)
    dispatch(trolleyProcessPayment.pay({
      trolley_id: trolleyId,
      gateway_payment_ref: publicId.current,
      total_price_paid_by_customer: totalPriceOwingByCustomer,
    })).then(onPaymentSuccessfulAndConfirmed)
  }

  const onPaymentError = (message) => {
    setSubmitting(false)
    setOpenSnackBar(true)
    console.error(message)
    sentryApi.error("revolut_checkout_error_response", message)
    onMessage("The payment processing failed")
    setDisableCheckoutButton(false)
    onClose()
  }

  const onPaymentCancel = () => {
    setSubmitting(false)
    setOpenSnackBar(true)
    onMessage("Payment cancelled!")
    setDisableCheckoutButton(false)
    onClose()
  }

  const onGeneralError = (error) => {
    setSubmitting(false)
    console.error(error)
    sentryApi.error("revolut_checkout_error", error)
    setOpenSnackBar(true)
    onMessage("Payment preparation failed")
    setDisableCheckoutButton(false)
    onClose()
  }

  return (
    <Modal
      open={true}
      onClose={processingPayment ? undefined : onClose}
      disableEnforceFocus
      disableAutoFocus
      disablePortal
      disableBackdropClick={processingPayment}
      disableEscapeKeyDown={processingPayment}
    >
      <div className={classes.modal}>
        { (loading || processingPayment) && (
          <div className={classes.spinnerContainer}>
            <CircularProgress
              style={{
                width: 60,
                height: 60,
                color: green[500],
              }}
            />
            { processingPayment && (
              <p className={classes.processingText}>Processing your payment...</p>
            )}
          </div>
        )}
        <div className={classes.content} style={{ display: (loading || processingPayment) ? "none" : "flex" }}>
          <div className={classes.header}>BossFotos</div>
          <div id="revolut-card-field" className={classes.cardField}></div>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Cardholder name"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
            />
            <Button
              onClick={handlePayWithCard}
              disabled={submitting}
              style={{ width: "100%" }}
            >
              {submitting ? "Processing..." : "Pay with Card"}
            </Button>
            { walletAvailable && (
              <div className={classes.divider}>
                <span className={classes.dividerText}>or</span>
              </div>
            )}
            <div id="revolut-wallet-buttons"></div>
          </div>
      </div>
    </Modal>
  )
}

const useStyles = makeStyles({
  modal: {
    position: "relative",
    padding: "20px",
    maxWidth: "380px",
    top: "50%",
    left: "50%",
    background: "white",
    transform: "translate(-50%, -50%)",
    borderRadius: "8px",
  },
  spinnerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "120px",
  },
  processingText: {
    marginTop: "16px",
    color: "#666",
    fontSize: "14px",
  },
  header: {
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center",
    color: "#333",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  cardField: {
    minHeight: "40px",
  },
  divider: {
    width: "100%",
    textAlign: "center",
    borderBottom: "1px solid #e0e0e0",
    lineHeight: "0.1em",
  },
  dividerText: {
    background: "white",
    padding: "0 10px",
    color: "#999",
    fontSize: "14px",
  },
})
